import type * as React from 'react'
import { createContext, Fragment, useRef } from 'react'

import { useTitleSignature } from '../../lib/signature/navigation'
import { cn } from '../../lib/utils'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from './breadcrumb'
import { RULED_HEAD, SATR } from './section'

/**
 * The screen header (DESIGN.md "Ruled sections", "Typography: Title").
 *
 * The trail, then the title on its satr with the screen's actions at the end
 * of the same line, then a sentence that says what the reader may do here:
 *
 *   Accounting › Journal
 *   Journal entries  [Posted]                    [Export] [New entry]
 *   ━━━━━━━━━━━━━━━━━━━━━━━━━──────────────────────────────────────────
 *   Every entry in the period, newest first.
 *
 * - The title is Kufam 600 at 24px ("The Big Moments Rule") in an `h1`. A code
 *   inside a title stays Alexandria: the caller wraps it in `font-sans`.
 * - The satr runs under the title and its adornment (a status badge), and
 *   continues as the 1px hairline to the end of the line. Actions stand 10px
 *   above the rule; below 900px they drop under it (see `RULED_HEAD`).
 * - Actions: put the primary action LAST, as DESIGN.md "Buttons" requires.
 *   This component does not reorder them.
 * - The description stops at 72 characters.
 *
 * The signature layer writes the title once per navigation (DESIGN.md "The
 * pen: the title writes itself"; `lib/signature/navigation.ts`): the router
 * starts it, and a title that mounts after its screen finished loading
 * claims it through `useTitleSignature`. Its hooks: the header is
 * `data-slot="page-header"`, the satr line `data-slot="page-header-satr"`
 * with `data-satr`, the heading `data-slot="page-title"`.
 *
 * The header carries no inline inset; the app shell's page column does.
 */

interface PageHeaderProps {
  /** Ancestor crumbs, in order. The title is appended as the final crumb. */
  trail?: readonly (string | { label: string; href?: string })[]
  title: React.ReactNode
  /** Sits on the satr beside the title, outside the heading's name: a status badge. */
  titleAdornment?: React.ReactNode
  /** The content under the rule: scope, capability, or a caveat. */
  description?: React.ReactNode
  /** End-aligned controls. Put the primary action last. */
  actions?: React.ReactNode
  /**
   * Kept for compatibility; it has no effect. The trail now sits in the
   * header's own flow, so there is nothing to pull flush.
   */
  flushTrail?: boolean
  className?: string
}

const PageHeaderLayoutContext = createContext(false)

/** Kept for compatibility with `PageShell` and older screens; it carries no layout now. */
function PageHeaderLayoutProvider({
  flushTrail,
  children,
}: {
  flushTrail: boolean
  children: React.ReactNode
}) {
  return (
    <PageHeaderLayoutContext.Provider value={flushTrail}>
      {children}
    </PageHeaderLayoutContext.Provider>
  )
}

function PageHeader({
  trail,
  title,
  titleAdornment,
  description,
  actions,
  className,
}: PageHeaderProps) {
  const titleRef = useRef<HTMLHeadingElement>(null)
  useTitleSignature(titleRef)

  return (
    <div data-slot="page-header" className={cn('mb-6 min-w-0 shrink-0', className)}>
      {trail && trail.length > 0 ? (
        <Breadcrumb className="mb-2">
          {/*
           * Item and separator are siblings, not nested: both render `li`,
           * and an `li` inside an `li` is invalid inside an `ol`.
           */}
          {trail.map((crumb, index) => {
            const label = typeof crumb === 'string' ? crumb : crumb.label
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: two crumbs may share a label; the position is the identity
              <Fragment key={`${index}-${label}`}>
                <BreadcrumbItem>
                  {typeof crumb === 'string' || !crumb.href ? (
                    label
                  ) : (
                    <BreadcrumbLink href={crumb.href}>{label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {/* On phones the title repeats the current crumb, so the trail ends on its parent. */}
                <BreadcrumbSeparator
                  className={index === trail.length - 1 ? 'max-sm:hidden' : undefined}
                />
              </Fragment>
            )
          })}
          <BreadcrumbItem current className="max-sm:hidden">
            {title}
          </BreadcrumbItem>
        </Breadcrumb>
      ) : null}

      <div data-slot="page-header-row" className={cn(RULED_HEAD, 'gap-x-6')}>
        <div
          data-slot="page-header-satr"
          data-satr=""
          className={cn(SATR, 'flex min-w-0 max-w-full flex-wrap items-center gap-x-3 gap-y-1')}
        >
          <h1
            ref={titleRef}
            data-slot="page-title"
            className="font-display text-title text-foreground min-w-0 break-words text-balance"
          >
            {title}
          </h1>
          {titleAdornment ? <div className="flex items-center gap-2">{titleAdornment}</div> : null}
        </div>
        {actions ? (
          <div
            data-slot="page-header-actions"
            className="ms-auto flex flex-wrap items-center gap-2 max-[900px]:ms-0"
          >
            {actions}
          </div>
        ) : null}
      </div>

      {description ? (
        <p
          data-slot="page-header-description"
          className="text-body text-muted-foreground mt-2.5 max-w-[72ch]"
        >
          {description}
        </p>
      ) : null}
    </div>
  )
}

/**
 * A record header's facts (DESIGN.md "Record headers and identifier cells"):
 * number, party, code, tax ID, dates and currency, each in a square cell, the
 * row of cells in one 1px `--border` frame with 4px outer corners on paper.
 * Two cells to a row below 900px.
 *
 * A posted or approved record's seal sits at the inline end of the facts; the
 * signature layer places it beside `data-slot="identifier-cells"`.
 */
function IdentifierCells({ className, ...props }: React.ComponentProps<'dl'>) {
  return (
    <dl
      data-slot="identifier-cells"
      className={cn(
        'border-border bg-card flex w-fit max-w-full flex-wrap rounded-sm border',
        'max-[900px]:w-full',
        className,
      )}
      {...props}
    />
  )
}

/** One fact: a 12px muted label over the value in the identifier style. */
function IdentifierCell({
  label,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & { label: React.ReactNode }) {
  return (
    <div
      data-slot="identifier-cell"
      className={cn(
        'border-border grid min-w-[110px] content-start gap-0.5 px-3.5 py-2',
        'border-s first:border-s-0',
        'max-[900px]:flex-[1_1_45%] max-[900px]:border-s-0',
        'max-[900px]:[&:nth-child(2n)]:border-s max-[900px]:[&:nth-child(n+3)]:border-t',
        className,
      )}
      {...props}
    >
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-identifier text-foreground min-w-0 break-words">{children}</dd>
    </div>
  )
}

export { IdentifierCell, IdentifierCells, PageHeader, PageHeaderLayoutProvider }
